import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Download, Pencil, ShieldCheck, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  useWarrantyDetail,
  useWarrantyClaims,
  useGenerateClaimPacket,
  warrantyDaysRemaining,
} from '@/hooks/useWarranties';
import { AddWarrantyDialog } from '@/components/warranty/AddWarrantyDialog';
import { FileClaimDialog } from '@/components/warranty/FileClaimDialog';

const COMPONENT_LABELS: Record<string, string> = {
  panels: 'Panels',
  inverter: 'Inverter',
  battery: 'Battery',
  generator: 'Generator',
  workmanship: 'Workmanship',
  third_party: 'Third Party',
};

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20',
  expired: 'bg-destructive/10 text-destructive border-destructive/20',
  claimed: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  transferred: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  voided: 'bg-muted text-muted-foreground border-muted',
};

const CLAIM_STATUS_STYLES: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  submitted: 'bg-blue-500/10 text-blue-400',
  in_review: 'bg-wt-amber-primary/10 text-wt-amber-primary',
  approved: 'bg-wt-green-primary/10 text-wt-green-primary',
  denied: 'bg-destructive/10 text-destructive',
  resolved: 'bg-wt-green-primary/10 text-wt-green-primary',
  cancelled: 'bg-muted text-muted-foreground',
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground text-right">{value}</span>
    </div>
  );
}

export default function WarrantyDetail() {
  const { propertyId, warrantyId } = useParams<{ propertyId: string; warrantyId: string }>();
  const navigate = useNavigate();
  const { data: warranty, isLoading } = useWarrantyDetail(warrantyId);
  const { data: claims } = useWarrantyClaims(warrantyId);
  const generatePacket = useGenerateClaimPacket();
  const [editOpen, setEditOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <p className="text-sm text-muted-foreground">Loading warranty...</p>
      </div>
    );
  }

  if (!warranty) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <p className="text-sm text-destructive">Warranty not found</p>
        <Button variant="ghost" size="sm" className="mt-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
    );
  }

  const days = warrantyDaysRemaining(warranty.warranty_end_date);
  const statusStyle = STATUS_STYLES[warranty.status] ?? '';

  const handleGeneratePacket = (claimId: string) => {
    generatePacket.mutate(
      { claimId, warrantyId: warranty.id, propertyId: warranty.property_id },
      {
        onSuccess: () => toast.success('Claim packet generated'),
        onError: (err) => toast.error(`Failed: ${err.message}`),
      },
    );
  };

  const handleDownloadPacket = async (packetPath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('service-docs')
        .createSignedUrl(packetPath, 300); // 5 min expiry
      if (error) throw error;
      window.open(data.signedUrl, '_blank');
    } catch {
      toast.error('Failed to download packet');
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/app/property/${propertyId}`)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Property
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-muted-foreground" />
            {COMPONENT_LABELS[warranty.component_type] ?? warranty.component_type} Warranty
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {warranty.provider_name}{warranty.model ? ` — ${warranty.model}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={statusStyle}>
            {warranty.status}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="w-3 h-3 mr-1" /> Edit
          </Button>
        </div>
      </div>

      {/* Warranty Details */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Warranty Details</CardTitle>
        </CardHeader>
        <CardContent>
          <InfoRow label="Manufacturer" value={warranty.manufacturer} />
          <InfoRow label="Model" value={warranty.model ?? '—'} />
          <InfoRow label="Type" value={warranty.warranty_type} />
          <InfoRow label="Term" value={
            [
              warranty.term_years ? `${warranty.term_years} years` : null,
              warranty.term_cycles ? `${warranty.term_cycles} cycles` : null,
            ].filter(Boolean).join(' / ') || '—'
          } />
          {warranty.capacity_floor_percent != null && (
            <InfoRow label="Capacity Floor" value={`${warranty.capacity_floor_percent}%`} />
          )}
          <InfoRow label="Install Date" value={formatDate(warranty.install_date)} />
          <InfoRow label="Start Date" value={formatDate(warranty.warranty_start_date)} />
          <InfoRow label="End Date" value={formatDate(warranty.warranty_end_date)} />
          {days !== null && (
            <InfoRow label="Remaining" value={
              days < 0
                ? <span className="text-destructive">{Math.abs(days)} days overdue</span>
                : <span>{days} days</span>
            } />
          )}
          <InfoRow label="OEM Reference" value={warranty.provider_warranty_id ?? '—'} />
          <InfoRow label="Registration" value={
            <span className="flex items-center gap-1">
              {warranty.registration_status}
              {warranty.registration_url && (
                <a href={warranty.registration_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3 h-3 text-primary" />
                </a>
              )}
            </span>
          } />
          {warranty.coverage_details && (
            <InfoRow label="Coverage" value={warranty.coverage_details} />
          )}
        </CardContent>
      </Card>

      {/* Claims */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Claims</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setClaimOpen(true)}>
              <FileText className="w-3 h-3 mr-1" /> File Claim
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {(!claims || claims.length === 0) ? (
            <p className="text-xs text-muted-foreground">No claims filed</p>
          ) : (
            claims.map(c => (
              <div key={c.id} className="flex items-center justify-between text-xs border-b border-border last:border-0 py-1.5">
                <div className="space-y-0.5">
                  <p className="font-medium">{c.claim_type.replace('_', ' ')}</p>
                  <p className="text-muted-foreground truncate max-w-[300px]">{c.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={`text-[10px] ${CLAIM_STATUS_STYLES[c.status] ?? ''}`}>
                    {c.status.replace('_', ' ')}
                  </Badge>
                  {c.packet_path ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px] px-1.5"
                      onClick={() => handleDownloadPacket(c.packet_path!)}
                    >
                      <Download className="w-2.5 h-2.5 mr-0.5" /> Download PDF
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px] px-1.5"
                      onClick={() => handleGeneratePacket(c.id)}
                      disabled={generatePacket.isPending}
                    >
                      Generate PDF
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AddWarrantyDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        propertyId={warranty.property_id}
        plantId={warranty.plant_id}
        editWarranty={warranty}
      />
      <FileClaimDialog
        open={claimOpen}
        onOpenChange={setClaimOpen}
        warrantyId={warranty.id}
        propertyId={warranty.property_id}
        componentType={COMPONENT_LABELS[warranty.component_type] ?? warranty.component_type}
        providerName={warranty.provider_name}
      />
    </div>
  );
}
